import { useMemo } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, List, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { PackingOperation, PackingTask } from '../schema';
import type { PackingListItemLike } from '../utils/taskDisplayHelpers';
import type { ItemLocationForLookup } from '../utils/taskCompletion';
import TaskRow from './TaskRow';

interface Props {
    operation: PackingOperation;
    tasks: PackingTask[];
    completedCount: number;
    totalCount: number;
    packingLists?: Map<string, PackingListItemLike[]>;
    allItemLocations: ItemLocationForLookup[];
}

export default function AmbiguousTasksSection({ operation, tasks, completedCount, totalCount, packingLists, allItemLocations }: Props) {
    /** Match PackingTasksList destination groups: category A–Z, then item name A–Z */
    const tasksByCategory = useMemo(() => {
        const byCategory = new Map<string, PackingTask[]>();
        for (const task of tasks) {
            const categoryName = task.itemType?.category?.name ?? 'Uncategorized';
            const list = byCategory.get(categoryName) ?? [];
            list.push(task);
            byCategory.set(categoryName, list);
        }
        return Array.from(byCategory.entries())
            .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map(([categoryName, catTasks]) => ({
                categoryName,
                tasks: [...catTasks].sort((a, b) =>
                    (a.itemType?.name ?? '').localeCompare(b.itemType?.name ?? '', undefined, { sensitivity: 'base' })
                ),
            }));
    }, [tasks]);

    const sourceEvents = operation.sourceEvents || [];
    const targetEvents = operation.targetEvents || [];

    // Get source event names
    const sourceEventNames = sourceEvents.map(e => e.name).join(', ');

    // Get target event names and trailers
    const targetEventNames = targetEvents.map(e => e.name).join(', ');

    // Build explanation text
    const explanationText = `The following items need to be verified. They may be in any of the trailers from ${sourceEventNames || 'the source events'}. Please locate them and confirm they are in the trailers for ${targetEventNames || 'the target events'}.`;

    return (
        <Accordion defaultExpanded sx={{ mb: 2, border: '2px dashed', borderColor: 'primary.light', bgcolor: 'action.hover' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <HelpOutlineIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Verify Items in Trailers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {completedCount} / {totalCount} completed
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    {/* Explanation */}
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">{explanationText}</Typography>
                    </Alert>

                    {/* Subtitle */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        These items may be in multiple trailers - please locate and confirm
                    </Typography>

                    {/* Task list: grouped by category (same as main task list under each destination) */}
                    <Box>
                        {tasksByCategory.map(({ categoryName, tasks: categoryTasks }) => (
                            <Box key={categoryName} sx={{ mb: 2 }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ px: { xs: 1, sm: 2 }, py: 0.5, fontWeight: 600 }}
                                >
                                    {categoryName}
                                </Typography>
                                <List disablePadding>
                                    {categoryTasks.map(task => (
                                        <Box
                                            key={task.id}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'primary.main',
                                                borderRadius: 1,
                                                mb: 1,
                                                bgcolor: 'background.paper',
                                            }}
                                        >
                                            <TaskRow
                                                task={task}
                                                operation={operation}
                                                allItemLocations={allItemLocations}
                                                packingLists={packingLists ?? undefined}
                                            />
                                        </Box>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
